<template>
<div id="app">
    <network-status-alert/>
    <notifications/>
    <fade-transition mode="out-in" :duration="100">
        <router-view
            v-loading.fullscreen.lock="isContentLoading"
            element-loading-background="rgba(0, 0, 0, 0.2)" />
    </fade-transition>
</div>
</template>
<script>
import globalMixin from '@/mixins/globalMixin'
import pageSizeMixin from '@/mixins/pageSizeMixin'
import { defaultFontSize } from '@/enum/defaultDashboardSettings'
import { version } from '../package.json';

export default {
    mixins: [globalMixin, pageSizeMixin],
    data() {
        return {
            settingNewColors: false
        }
    },
    provide() {
        const appData = {}

        Object.defineProperty(appData, 'settingNewColors', {
            enumerable: true,
            get: () => this.settingNewColors,
        })

        return {
            appData,
        }
    },
    components: {
        NetworkStatusAlert: () => import('@/components/Common/NetworkStatusAlert')
    },
    async created() {
        document.title = `Realtime Monitor v${version}`
        try {
            await this.$store.dispatch('dashboards/setContentLoading', true)
            await this.$store.dispatch('entities/getEntitiesList')

            try {
                await this.$store.dispatch('lang/getLanguages')
            } finally {}

            const activeLanguage = this.$store.getters['lang/getActiveLanguage'] || 'en'

            await this.$store.dispatch('lang/setLanguage', activeLanguage)

            if (activeLanguage && process.env.NODE_ENV === 'production') {
                const locale = activeLanguage.locale
                this.$i18n.locale = locale
                this.$i18n.mergeLocaleMessage(locale, this.$store.state.lang.translations)
            }

            await this.$store.dispatch('dashboards/getDashboards')
            await this.$store.dispatch('templatesCategory/getAllTemplatesCategory')

            if (!this.activeDashboard) {
                await this.$store.dispatch('dashboards/selectDashboard')
            } else {
                const dashboardExist = this.allDashboards.some(dashboard => {
                    return dashboard.DashboardID === this.activeDashboard.DashboardID
                })

                if (dashboardExist) {
                    await this.$store.dispatch('dashboards/selectDashboard')
                } else {
                    await this.$store.dispatch('dashboards/selectDashboard', this.allDashboards[this.allDashboards.length - 1])
                }
            }

            await this.$store.dispatch('layout/setupActiveLayout')
            await this.$store.dispatch('templatesCategory/getAllTemplateDictionaries')
            await this.$store.dispatch('layout/setupLayouts')
            await this.$store.dispatch('layout/getGlobalLayout')
            await this.$store.dispatch('widgetTemplate/getAllWidgetTemplates')
            await this.$store.dispatch('layout/setFavicon')
        } finally {
            await this.$store.dispatch('dashboards/setContentLoading', false)
        }
    },
    computed: {
        layout() {
            return this.$store.getters['layout/getActiveLayout']
        },
        colors() {
            return this.$store.getters['layout/colors']('activeLayout')
        },
        fontSize() {
            return this.$store.getters['layout/baseFontSize']('activeLayout')
        },
        activeDashboard() {
            return this.$store.getters['dashboards/getActiveDashboard']
        },
        allDashboards() {
            return this.$store.getters['dashboards/getAllDashboards']
        },
        isContentLoading() {
            return this.$store.state.dashboards.contentLoading && window.location.pathname !== '/'
        }
    },
    methods: {
        initMainColorsVars(colors) {
            this.settingNewColors = true
            for (let color in colors) {
                document.documentElement.style.setProperty('--' + color + '-color', colors[color]);
            }

            this.$nextTick(() => {
                this.settingNewColors = false
            })
        },
        initMainFontSizeVar(fontSize) {
            fontSize = fontSize || defaultFontSize
            document.documentElement.style.setProperty('--font-size-xs', (fontSize - 4) + 'px');
            document.documentElement.style.setProperty('--font-size-sm', (fontSize - 2) + 'px');
            document.documentElement.style.setProperty('--font-size-base', fontSize + 'px');
            document.documentElement.style.setProperty('--font-size-lg', (fontSize + 2) + 'px');
            document.documentElement.style.setProperty('--font-size-xl', (fontSize + 4) + 'px');
            document.documentElement.style.setProperty('--font-size-2xl', (fontSize + 6) + 'px');
            document.documentElement.style.setProperty('--font-size-3xl', (fontSize + 12) + 'px');
        }
    },
    watch: {
        colors: {
            deep: true,
            immediate: true,
            handler: function (values) {
                if (!values) {
                    return
                }
                this.initMainColorsVars(values)
            },
        },
        fontSize: {
            immediate: true,
            handler: function (value) {
                this.initMainFontSizeVar(value)
            },
        },
        activeDashboard: {
            immediate: true,
            deep: true,
            handler: async function (oldVal, newVal) {
                if (oldVal && newVal && oldVal.DashboardID !== newVal.DashboardID) {
                    await this.$store.dispatch('layout/setupActiveLayout')
                }
            }
        }
    }
}
</script>

<style lang="scss">
[dir="rtl"] .popper__arrow::after {
    display: none !important;
}
.list-popover-height .max-w-lg, .list-popover-height .max-w-xs {
    max-height: 300px !important;
    overflow-y: auto;
}
.el-color-picker__panel {
  background-color: var(--secondaryBgColor-color) !important;
}
.el-color-picker__panel .el-input__inner {
    background: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
.el-popover {
    color: var(--mainTextColor-color) !important;
    background: var(--mainBgColor-color) !important;
}
.el-input__inner {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__item {
    color: var(--mainTextColor-color) !important;
}
.el-button.el-button--default {
    background: var(--itemsMainColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-input-number__decrease, .el-input-number__increase {
    background: var(--itemsSecondaryColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
 .el-input-number .el-input__inner {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.highcharts-label text {
    color: var(--mainTextColor-color) !important;
}
.el-checkbox__input.is-checked + span.el-checkbox__label, .el-checkbox__label {
    color: var(--mainTextColor-color) !important;
}
.el-range-input {
    background: var(--mainBgColor-color);
    color: var(--mainTextColor-color) !important;
}
.el-picker-panel__body {
    background: var(--mainBgColor-color);
}
.el-form-item__label {
    color: var(--mainTextColor-color) !important;
}
.el-tag, .el-tag.el-tag--info {
    background: var(--itemsMainColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__item.is-disabled:hover {
    background: var(--secondaryBgColor-color) !important;
}
.el-range-separator {
    color: var(--mainTextColor-color) !important;
}
.el-date-range-picker__header, .el-picker-panel__icon-btn {
    color: var(--mainTextColor-color) !important;
}
.el-date-table th {
    color: var(--mainTextColor-color) !important;
}
.el-date-table td.disabled div {
    background-color: transparent !important;
}
.el-date-table th {
    background-color: var(--secondaryBgColor-color) !important;
}
.el-dropdown-menu {
    background-color: var(--secondaryBgColor-color) !important;
}
.redactor-box.redactor-styles-on {
    background: var(--mainBgColor-color) !important;
}
tspan {
    color: var(--mainTextColor-color) !important;
    fill: var(--mainTextColor-color) !important;
}
body {
    background: var(--secondaryBgColor-color);
}
.el-select-dropdown__item.hover, .el-select-dropdown__item:hover {
    background-color: var(--mainBgColor-color) !important;
}
.el-select-dropdown.is-multiple .el-select-dropdown__item.selected {
    background: var(--secondaryBgColor-color) !important;
}
.el-time-spinner__item, .el-time-range-picker__header {
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__empty {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-time-spinner {
    background: var(--secondaryBgColor-color) !important;
}
.el-time-spinner__item:hover {
    background: var(--mainBgColor-color) !important;
}
.el-time-panel__content::after, .el-time-panel__content::before {
    border-top: 1px solid var(--mainTextColor-color) !important;
    border-bottom: 1px solid var(--mainTextColor-color) !important;
    z-index: 2 !important;
}
.el-select-dropdown {
    background-color: var(--secondaryBgColor-color) !important;
}
.el-notification {
    background-color: var(--mainBgColor-color) !important;
}
.el-notification__content, .el-notification__title {
    color: var(--mainTextColor-color) !important;
}
.el-dropdown-menu__item {
    color: var(--mainTextColor-color) !important;
}
.el-dropdown-menu__item:focus, .el-dropdown-menu__item:not(.is-disabled):hover {
    background-color: var(--mainBgColor-color) !important;
}
.el-message-box {
    background-color: var(--mainBgColor-color) !important;
}.el-message-box__message, .el-message-box__title {
    color: var(--mainTextColor-color) !important;
}
</style>
